import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as contentActions from '~actions/content-actions';

import AccountLink from '~components/general/account-link';
import ContentLink from '~components/general/content-link';
import GettingMore from '~components/general/getting-more';
import IString from '~components/general/i-string';
import { useSortContext, withSortContext } from '~components/general/sort/sort-context';
import SortableHeader from '~components/general/sortable-header';
import SpaceLink from '~components/general/space-link';
import Time from '~components/general/time';

import * as contentSortBy from '~constants/content-sortby';
import * as sortOrder from '~constants/sort-order';

import * as userSelectors from '~selectors/user-selectors';

export function UserAuthoredContent({ userId }) {
  const dispatch = useDispatch();

  const content = useSelector(userSelectors.getUserAuthoredContent);
  const loading = useSelector(userSelectors.isUserAuthoredContentLoading);

  const { order, sortBy } = useSortContext();

  const emptyList = !loading && content?.length === 0;

  useEffect(() => {
    if (userId) {
      dispatch(contentActions.fetchContentByAuthor(userId, `${sortBy}:${order}`));
    }
    // returned function will be called on component unmount
    return () =>
      dispatch(contentActions.clearCurrentUserAuthoredContent());
  }, [ dispatch, order, sortBy, userId ]);

  return (
    <div className="splitColumns withMobileMargins">
      <div className="column100">

        <div className="sectionTitle flex">
          <IString stringKey="user.authoredContent.title" />
        </div>

        <div className="tableContainer">
          { emptyList
            ? <div className="emptyListMessage"><IString stringKey="general.empty" /></div>
            : <>
              <table className="contentListing sortableColumns allTightRight">
                <thead>
                  <tr>
                    <SortableHeader field={contentSortBy.ID}>
                      <IString stringKey="general.id" />
                    </SortableHeader>
                    <SortableHeader field={contentSortBy.NAME}>
                      <IString stringKey="general.name" />
                    </SortableHeader>
                    <th className="unsortable">
                      <IString stringKey="general.type" />
                    </th>
                    <th className="unsortable">
                      <IString stringKey="general.state" />
                    </th>
                    <SortableHeader field={contentSortBy.SPACE}>
                      <IString stringKey="general.space" />
                    </SortableHeader>
                    <SortableHeader field={contentSortBy.ACCOUNT}>
                      <IString stringKey="general.account" />
                    </SortableHeader>
                    <SortableHeader field={contentSortBy.CREATED}>
                      <IString stringKey="general.created" />
                    </SortableHeader>
                    <SortableHeader field={contentSortBy.UPDATED}>
                      <IString stringKey="general.updated" />
                    </SortableHeader>
                  </tr>
                </thead>
                <tbody>
                  {content && content.map(c =>
                    <tr key={c.id}>
                      <td><ContentLink contentId={c.id} /></td>
                      <td>{c.name}</td>
                      <td>{c.content_type}</td>
                      <td>{c.state}</td>
                      <td>{c.space_id ? <SpaceLink spaceId={c.space_id} /> : <em><IString stringKey="general.notSet" /></em>}</td>
                      <td>{c.account_id && <AccountLink accountId={c.account_id} /> }</td>
                      <td><Time value={c.created_time} /></td>
                      <td><Time value={c.updated_time} /></td>
                    </tr>
                )}
                </tbody>
              </table>
              {loading && <GettingMore label={<IString stringKey="general.loading" />} />}
            </>
          }
        </div>
      </div>
    </div>
  );
}

UserAuthoredContent.propTypes = {
  userId: PropTypes.number
};

export default withSortContext(UserAuthoredContent, { order: sortOrder.DESC, sortBy: contentSortBy.CREATED });
